/**
 * Source of truth for the public homepage copy, SEO metadata and internal
 * linking. Copy here is used verbatim by <MarketingHome /> and the "/" route
 * head(); link keys resolve to routes that already exist in this project.
 */

export const homepageSEO = {
  title: "JEE Tracker, NEET Tracker & Exam Preparation Tracker | The Quantum Minds",
  description:
    "Track JEE, NEET, CBSE, KCET, MHT CET and more with The Quantum Minds. Track syllabus, lectures, revision, questions, tests and preparation progress in one place.",

  keywords: [
    "JEE tracker",
    "JEE preparation tracker",
    "JEE syllabus tracker",
    "JEE revision tracker",
    "NEET tracker",
    "NEET preparation tracker",
    "exam preparation tracker",
    "study tracker",
    "syllabus tracker",
    "test tracker",
    "performance analytics",
    "competitive exam preparation",
  ],

  canonical: "https://thequantumminds.in/",
};

/** Logical link keys resolved to routes that already exist in this project. */
export const internalLinkMap = {
  home: "/",
  preparationTracker: "/jee-preparation-tracker",
  revisionPlanner: "/revision-tracker",
  questions: "/questions",
  testTracker: "/test-tracker",
  analytics: "/tests/performance",
  tracker360: "/tracker-360",
  simulations: "/simulations",
  virtualLabs: "/labs",
  whatIfLab: "/what-if",
  studyToolkit: "/toolkit",
  dailyPlan: "/study-planner",
  chapterPrerequisites: "/prerequisites",
  jee: "/jee",
  neet: "/neet",
  cbse: "/cbse",
  isc: "/isc",
  icse: "/icse",
  kcet: "/kcet",
  mhtcet: "/mht-cet",
  // Canonical tracker entry points. These paths resolve to the matching exam
  // preparation page, so every link below is a real, crawlable internal URL.
  jeeTracker: "/tracker/jee-main",
  neetTracker: "/tracker/neet",
  cbseTracker: "/tracker/cbse",
  kcetTracker: "/tracker/kcet",
  mhtcetTracker: "/tracker/mht-cet",
} as const;


export type LinkKey = keyof typeof internalLinkMap;

export const homepageContent = {
  hero: {
    eyebrow: "JEE • NEET • CBSE • ISC • ICSE • KCET • MHT CET",
    h1: "JEE, NEET & Exam Preparation Tracker for Students",
    description:
      "Track your syllabus, chapters, lectures, revision, questions, tests and preparation progress in one connected study system.",
    secondaryDescription:
      "The Quantum Minds brings planning, preparation tracking, revision, practice and performance analytics together so you always know what to study, what you have completed and what needs attention.",
    examLine: "Built for JEE, NEET, CBSE, ISC, ICSE, KCET, MHT CET and more.",
    primaryCTA: { text: "Start Tracking Free" },
    secondaryCTA: { text: "Explore Everything" },
    valueLine: "Track your preparation. See your progress. Know what to do next.",
    trustLine:
      "Explore the platform freely. Create an account when you want to save and sync your progress.",
  },
  trackerOverview: {
    eyebrow: "PREPARATION TRACKER",
    h2: "One Tracker for Your Entire JEE Preparation",
    paragraphs: [
      "A good JEE preparation plan is not only about making a timetable. You need to know which chapters are complete, which lectures you have watched, what needs revision, how many questions you have solved and how your test performance is changing.",
      "The Quantum Minds JEE Tracker brings these parts of your preparation together in one place, so your progress is based on what you actually complete — not just what you planned.",
    ],
    cards: [
      {
        title: "Syllabus Tracker",
        description:
          "Track chapters and topics as you complete them and see your preparation progress clearly.",
        icon: "layers",
        linkKey: "preparationTracker",
        linkLabel: "Explore syllabus tracking",
      },
      {
        title: "Lecture Tracker",
        description:
          "Record the lectures you actually study and keep your learning progress organised.",
        icon: "graduation",
        linkKey: "dailyPlan",
        linkLabel: "Plan your lectures",
      },
      {
        title: "Revision Tracker",
        description:
          "Turn completed learning into a structured revision workflow instead of leaving revision for later.",
        icon: "calendar",
        linkKey: "revisionPlanner",
        linkLabel: "Open revision planner",
      },
      {
        title: "Question Tracker",
        description:
          "Set practice targets and track the questions you actually solve chapter by chapter.",
        icon: "clipboard",
        linkKey: "questions",
        linkLabel: "Track question practice",
      },
      {
        title: "Test Tracker",
        description:
          "Record tests, scores and subject-wise performance to understand where you need more work.",
        icon: "flask",
        linkKey: "testTracker",
        linkLabel: "Open test tracker",
      },
      {
        title: "Performance Analytics",
        description:
          "Use preparation and test data to identify weak areas, trends and improvement opportunities.",
        icon: "trending",
        linkKey: "analytics",
        linkLabel: "See performance analytics",
      },
    ],
  },
  whatIsTracker: {
    eyebrow: "JEE PREPARATION",
    h2: "What Is a JEE Tracker?",
    paragraphs: [
      "A JEE Tracker is a study-management tool that helps students track the progress of their JEE preparation in one place. Instead of relying on notebooks, spreadsheets or multiple disconnected apps, students can organise their syllabus, chapters, lectures, revision, question practice and tests through a connected preparation workflow.",
      "With The Quantum Minds JEE Tracker, you can keep track of what you have completed, what is pending, what needs revision and how your preparation is progressing over time.",
    ],
    checklist: [
      "Track JEE syllabus and chapters",
      "Track lectures and learning progress",
      "Plan and track revision",
      "Track question practice",
      "Record tests and scores",
      "Understand preparation performance",
    ],
    ctaLabel: "Explore JEE Tracker",
    ctaLinkKey: "jeeTracker",
  },
  howItWorks: {
    eyebrow: "THE WORKFLOW",
    h2: "How the JEE Preparation Tracker Works",
    description:
      "Your preparation becomes easier to manage when planning, learning, practice, revision and testing are connected.",
    steps: [
      {
        number: "01",
        title: "PLAN",
        description: "Organise your syllabus and decide what you need to cover.",
      },
      {
        number: "02",
        title: "LEARN",
        description: "Track the chapters and lectures you actually study.",
      },
      {
        number: "03",
        title: "PRACTICE",
        description: "Record question practice and build consistency chapter by chapter.",
      },
      {
        number: "04",
        title: "REVISE",
        description: "Bring completed learning back into your revision workflow.",
      },
      {
        number: "05",
        title: "TEST",
        description: "Track tests and subject-wise scores to measure preparation.",
      },
      {
        number: "06",
        title: "ANALYZE",
        description:
          "Use your preparation data to identify weak areas and decide what to work on next.",
      },
    ],
    closingLine: "One connected workflow. Less guesswork. Better preparation visibility.",
    ctaLabel: "Start Tracking Free",
  },
  trackerBenefits: {
    eyebrow: "WHY TRACKING HELPS",
    h2: "Why Use a JEE Preparation Tracker?",
    intro:
      "JEE preparation involves hundreds of chapters, lectures, practice sessions, revisions and tests. Without a clear tracking system, it is easy to lose sight of what is complete and what still needs attention.",
    items: [
      {
        title: "Know What You've Completed",
        description:
          "See your actual chapter and lecture progress instead of relying on memory.",
      },
      {
        title: "Keep Revision Visible",
        description:
          "Bring completed topics back into your revision workflow so important concepts do not disappear after the first study session.",
      },
      {
        title: "Track Practice and Tests",
        description:
          "Record the questions you solve and the tests you take to build a clearer picture of your preparation.",
      },
      {
        title: "Find What Needs Attention",
        description:
          "Use preparation and performance data to identify weak subjects, unfinished chapters and areas that need more work.",
      },
    ],
  },
  comparison: {
    h2: "More Than a JEE Study To-Do List",
    paragraph:
      "A simple to-do list tells you what you planned to do. A preparation tracker helps you understand what you actually completed, what needs revision, how much you have practised and how your test performance is changing.",
    todo: {
      title: "Simple to-do list",
      points: [
        "Planned tasks",
        "Basic checkmarks",
        "Limited progress visibility",
        "No connected revision workflow",
        "No preparation analytics",
      ],
    },
    tracker: {
      title: "The Quantum Minds Tracker",
      points: [
        "Syllabus progress",
        "Lecture tracking",
        "Revision workflow",
        "Question practice tracking",
        "Test tracking",
        "Performance analytics",
        "Connected preparation system",
      ],
    },
  },
  examTrackers: {
    h3: "Open your exam tracker",
    items: [
      {
        name: "JEE Tracker",
        description:
          "Track your JEE syllabus, chapters, lectures, revision, questions, tests and preparation progress.",
        cta: "Open JEE Tracker",
        linkKey: "jeeTracker",
      },
      {
        name: "NEET Tracker",
        description:
          "Organise your NEET preparation with syllabus, study progress, revision, practice and test tracking.",
        cta: "Open NEET Tracker",
        linkKey: "neetTracker",
      },
      {
        name: "CBSE Tracker",
        description:
          "Keep your CBSE chapters, study progress, revision and tests organised in one place.",
        cta: "Open CBSE Tracker",
        linkKey: "cbseTracker",
      },
      {
        name: "KCET Tracker",
        description:
          "Track KCET preparation, revision, practice and test progress through one connected workflow.",
        cta: "Open KCET Tracker",
        linkKey: "kcetTracker",
      },
      {
        name: "MHT CET Tracker",
        description:
          "Organise your MHT CET preparation with study, revision, practice and performance tracking.",
        cta: "Open MHT CET Tracker",
        linkKey: "mhtcetTracker",
      },
    ],
  },

  whatIsIt: {
    eyebrow: "THE IDEA",
    h2: "What Is The Quantum Minds?",
    paragraphs: [
      "The Quantum Minds is an all-in-one study and preparation platform built to help students organize, track, revise and improve their preparation.",
      "Instead of using separate tools for planning, lectures, revision, questions, tests and performance, students can bring their preparation into one connected system.",
      "Whether you are preparing for JEE, NEET, CBSE, ISC, ICSE, KCET or MHT CET, the goal is simple: know what you need to study, what you have completed, what needs revision and where you need to improve.",
    ],
  },
  whyStudentsNeedIt: {
    eyebrow: "THE PROBLEM",
    h2: "Why Students Need a Better Preparation System",
    intro:
      "Most students do not struggle because they have no plans. They struggle because their plan, actual work, revision and test performance live in different places.",
    problems: [
      {
        problem: "You forget what you actually completed.",
        solution: "Track chapters and lectures so your progress reflects real work.",
      },
      {
        problem: "Revision keeps getting postponed.",
        solution: "Turn completed learning into a structured revision workflow.",
      },
      {
        problem: "Backlogs quietly keep growing.",
        solution: "Keep pending work visible and carry it forward instead of losing it.",
      },
      {
        problem: "Question practice becomes inconsistent.",
        solution: "Set and track question-practice targets chapter by chapter.",
      },
      {
        problem: "Test marks tell you what happened, not always why.",
        solution:
          "Use performance data to understand subjects, scores and improvement trends.",
      },
      {
        problem: "You use too many disconnected tools.",
        solution:
          "Bring planning, tracking, revision, testing and study tools into one ecosystem.",
      },
    ],
  },
  preparationJourney: {
    eyebrow: "ONE CONNECTED WORKFLOW",
    h2: "From Planning to Progress",
    description:
      "Your preparation is not just about making a timetable. It is a continuous cycle of planning, learning, practice, revision, testing and improvement.",
    stages: [
      {
        number: "01",
        title: "PLAN",
        description: "Decide what you need to cover and organise your preparation.",
        linkLabel: "Explore Preparation Tracking",
        linkKey: "preparationTracker",
      },
      {
        number: "02",
        title: "LEARN",
        description: "Track lectures and the chapters you are actually studying.",
        linkLabel: "Track Your Lectures",
        linkKey: "preparationTracker",
      },
      {
        number: "03",
        title: "PRACTICE",
        description: "Track the questions you solve instead of only planning them.",
        linkLabel: "Practice & Track Questions",
        linkKey: "questions",
      },
      {
        number: "04",
        title: "REVISE",
        description: "Bring completed learning back into your revision workflow.",
        linkLabel: "Explore Revision Planner",
        linkKey: "revisionPlanner",
      },
      {
        number: "05",
        title: "TEST",
        description: "Plan tests, record scores and understand your performance.",
        linkLabel: "Explore Test Tracker",
        linkKey: "testTracker",
      },
      {
        number: "06",
        title: "ANALYZE",
        description: "Find patterns, weak areas and improvement trends.",
        linkLabel: "Explore Performance Analytics",
        linkKey: "analytics",
      },
      {
        number: "07",
        title: "IMPROVE",
        description:
          "Use what your preparation data tells you to decide what comes next.",
        linkLabel: "Explore Tracker 360",
        linkKey: "tracker360",
      },
    ],
  },
  exams: {
    eyebrow: "BUILT FOR DIFFERENT PREPARATION GOALS",
    h2: "JEE, NEET & Exam Preparation Tracking in One System",
    intro:
      "Whether you are preparing for JEE, NEET, CBSE, ISC, ICSE, KCET or MHT CET, The Quantum Minds helps you organise your syllabus, learning, revision, practice and tests in one preparation system.",

    exams: [
      {
        name: "JEE",
        description:
          "Track syllabus, lectures, revision, questions, tests and preparation progress.",
        linkKey: "jee",
      },
      {
        name: "NEET",
        description:
          "Organise subjects, study progress, revision, practice and test preparation.",
        linkKey: "neet",
      },
      {
        name: "CBSE",
        description:
          "Keep chapters, study progress, revision and tests organised in one place.",
        linkKey: "cbse",
      },
      {
        name: "ISC",
        description:
          "Build a structured study workflow around your syllabus, revision and tests.",
        linkKey: "isc",
      },
      {
        name: "ICSE",
        description:
          "Track your study progress and keep revision and preparation organised.",
        linkKey: "icse",
      },
      {
        name: "KCET",
        description:
          "Organise your preparation, practice, revision and tests with one system.",
        linkKey: "kcet",
      },
      {
        name: "MHT CET",
        description:
          "Keep your preparation, revision, practice and performance organised.",
        linkKey: "mhtcet",
      },
    ],
  },
  features: {
    eyebrow: "THE COMPLETE SYSTEM",
    h2: "Everything Your Preparation Needs, In One Place",
    intro:
      "Instead of switching between separate apps for every part of your preparation, connect the important parts of your study workflow in one place.",
    items: [
      {
        title: "Syllabus & Lecture Tracking",
        description:
          "Add chapters, track lectures as you finish them and see real preparation progress instead of relying on rough estimates.",
        linkKey: "preparationTracker",
        linkLabel: "Track your preparation",
      },
      {
        title: "Spaced Revision Planner",
        description:
          "Completed learning can come back into your revision workflow so important concepts do not disappear after the lecture.",
        linkKey: "revisionPlanner",
        linkLabel: "Plan your revision",
      },
      {
        title: "Question Practice",
        description:
          "Set chapter-wise practice targets and record the questions you actually solve.",
        linkKey: "questions",
        linkLabel: "Explore question tracking",
      },
      {
        title: "Test Tracker",
        description:
          "Plan chapter-wise preparation before tests and record your subject-wise performance afterwards.",
        linkKey: "testTracker",
        linkLabel: "Track your tests",
      },
      {
        title: "Performance Analytics",
        description:
          "Understand averages, best and worst scores, subject-wise performance and improvement trends.",
        linkKey: "analytics",
        linkLabel: "Understand your performance",
      },
      {
        title: "Interactive Simulations",
        description:
          "Explore concepts visually through interactive simulations instead of relying only on static explanations.",
        linkKey: "simulations",
        linkLabel: "Explore simulations",
      },
      {
        title: "Virtual Labs",
        description: "Run experiments and explore concepts directly in your browser.",
        linkKey: "virtualLabs",
        linkLabel: "Explore virtual labs",
      },
      {
        title: "What If Lab",
        description:
          "Explore physics thought experiments and understand how changing conditions can affect outcomes.",
        linkKey: "whatIfLab",
        linkLabel: "Explore What If Lab",
      },
      {
        title: "Study Toolkit",
        description:
          "Access useful study tools such as formulas, calculators, converters, flashcards and other utilities.",
        linkKey: "studyToolkit",
        linkLabel: "Open study toolkit",
      },
      {
        title: "Daily Plan & Streaks",
        description:
          "Bring revision, practice and test tasks together into a clearer daily study workflow.",
        linkKey: "dailyPlan",
        linkLabel: "Build your daily plan",
      },
      {
        title: "Tracker 360",
        description:
          "Get a broader view of your preparation and progress through the connected tracking system.",
        linkKey: "tracker360",
        linkLabel: "Explore Tracker 360",
      },
    ],
  },
  affordability: {
    eyebrow: "BUILT FOR STUDENTS",
    h2: "A Complete Preparation System Shouldn't Be Expensive",
    paragraphs: [
      "Students should not need a collection of expensive tools just to organise their preparation.",
      "The Quantum Minds is designed to keep powerful preparation and tracking tools accessible to students, with a free starting experience and affordable options where applicable.",
      "Explore first. Create an account when you are ready to save and sync your preparation.",
    ],
    points: [
      "Free to explore",
      "Student-focused pricing",
      "No unnecessary tool switching",
      "Multiple preparation tools in one ecosystem",
    ],
    cta: "Explore The Quantum Minds",
  },
  useCases: {
    eyebrow: "MADE FOR YOUR STAGE OF PREPARATION",
    h2: "Built for Every Stage of Preparation",
    items: [
      {
        title: "Starting Your Preparation",
        description:
          "Build a clear system before your syllabus and backlog become difficult to manage.",
      },
      {
        title: "Midway Through Preparation",
        description:
          "Understand what is complete, what needs revision and where your time is going.",
      },
      {
        title: "Managing a Backlog",
        description: "Keep unfinished work visible and turn it into a manageable plan.",
      },
      {
        title: "Preparing for Tests",
        description:
          "Organise the chapters covered by upcoming tests and revise with purpose.",
      },
      {
        title: "Improving Performance",
        description:
          "Use your test and preparation data to identify patterns and weak areas.",
      },
    ],
  },
  discovery: {
    eyebrow: "EXPLORE THE ECOSYSTEM",
    h2: "What Should You Explore Next?",
    description:
      "The Quantum Minds is designed as a connected preparation system. Start with one tool and discover the next one when you need it.",
    journeys: [
      {
        from: "Preparation Tracker",
        question:
          "You have tracked your lectures. What should happen after you finish learning?",
        next: "Revision Planner",
        linkKey: "revisionPlanner",
      },
      {
        from: "Revision Planner",
        question:
          "You are revising consistently. How do you know whether your preparation is improving?",
        next: "Test Tracker",
        linkKey: "testTracker",
      },
      {
        from: "Test Tracker",
        question: "You have your marks. Which subjects and areas need more attention?",
        next: "Performance Analytics",
        linkKey: "analytics",
      },
      {
        from: "Performance Analytics",
        question: "You found a weak area. What should you study before moving ahead?",
        next: "Chapter Prerequisites",
        linkKey: "chapterPrerequisites",
      },
      {
        from: "Chapter Prerequisites",
        question:
          "You understand what to study first. Want to explore the concept visually?",
        next: "Interactive Simulations",
        linkKey: "simulations",
      },
      {
        from: "Interactive Simulations",
        question: "Want to go beyond visualisation and experiment with concepts?",
        next: "Virtual Labs",
        linkKey: "virtualLabs",
      },
    ],
  },
  examSelector: {
    h2: "Which Exam Are You Preparing For?",
    description:
      "Choose your exam and open the preparation tracker built around your study workflow.",
    exams: [
      { name: "JEE", linkKey: "jeeTracker" },
      { name: "NEET", linkKey: "neetTracker" },
      { name: "CBSE", linkKey: "cbseTracker" },
      { name: "ISC", linkKey: "isc" },
      { name: "ICSE", linkKey: "icse" },
      { name: "KCET", linkKey: "kcetTracker" },
      { name: "MHT CET", linkKey: "mhtcetTracker" },
    ],
  },

  faq: {
    h2: "Frequently Asked Questions",
    items: [
      {
        question: "What is The Quantum Minds?",
        answer:
          "The Quantum Minds is an all-in-one study and preparation platform that helps students plan, track, revise, practise, test and understand their preparation.",
      },
      {
        question: "What is a JEE Tracker?",
        answer:
          "A JEE Tracker is a study-management tool for tracking JEE preparation progress in one place — syllabus, chapters, lectures, revision, question practice and tests — instead of spreading them across notebooks, spreadsheets and separate apps.",
      },
      {
        question: "Can I track my JEE syllabus and chapters?",
        answer:
          "Yes. Add your chapters subject by subject and mark them as you complete them, so your syllabus progress reflects real work rather than an estimate.",
      },
      {
        question: "Can I track lectures and study progress?",
        answer:
          "Yes. Lectures can be tracked chapter by chapter, so you always know which lecture comes next and how much of a chapter is complete.",
      },
      {
        question: "Can I track NEET preparation?",
        answer:
          "Yes. NEET preparation uses the same workflow — syllabus, lectures, revision, question practice, tests and performance analytics — across Physics, Chemistry and Biology.",
      },

      {
        question: "Who can use The Quantum Minds?",
        answer:
          "Students preparing for exams such as JEE, NEET, CBSE, ISC, ICSE, KCET and MHT CET can use the platform and its relevant preparation tools.",
      },
      {
        question: "Is The Quantum Minds free?",
        answer:
          "You can explore the platform without committing to an account. A free plan is available, and premium features are listed on the pricing page.",
      },
      {
        question: "Do I need an account to explore the platform?",
        answer:
          "No. The platform can be explored first. Create an account when you want to save and sync your preparation progress.",
      },
      {
        question: "Can I track my syllabus and lectures?",
        answer:
          "Yes. The preparation tracking system lets you organise chapters and track lectures as you complete them.",
      },
      {
        question: "Can I plan and track revision?",
        answer:
          "Yes. The revision workflow helps you organise completed learning for future revision.",
      },
      {
        question: "Can I track questions and tests?",
        answer:
          "Yes. You can track question practice and organise tests and their performance through the relevant tools.",
      },
      {
        question: "Can I see my preparation and performance progress?",
        answer:
          "Yes. The platform provides progress and performance views based on the data you record.",
      },
      {
        question: "Can I use The Quantum Minds for JEE and NEET?",
        answer:
          "Yes. JEE and NEET are among the exams supported by the platform. Other supported exam paths are also available.",
      },
      {
        question: "Which exams are supported?",
        answer:
          "The platform currently supports JEE, NEET, CBSE, ISC, ICSE, KCET and MHT CET.",
      },
      {
        question: "Can I access The Quantum Minds from my phone and laptop?",
        answer:
          "Yes. The platform works across modern phones, tablets and laptops in the browser, and can be installed as an app.",
      },
    ],
  },
  finalCTA: {
    eyebrow: "YOUR PREPARATION. ONE SYSTEM.",
    h2: "Stop Managing Your Preparation Everywhere",
    description:
      "Track your syllabus, lectures, revision, questions, tests and preparation progress in one connected system.",

    primaryCTA: "Start Tracking Free",
    secondaryCTA: "Explore The Quantum Minds",
  },
  footerQuote: {
    quote: "You don't need more time. You need a better way to use it.",
    brand: "THE QUANTUM MINDS",
  },
} as const;
